/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, signal} from '@angular/core';
import * as _ from 'lodash';

import {OBJECT_TYPE, ObjectTypeFinder} from '../../common/constants/object-type';
import {Uuid} from '../../../components/util/uuid';
import {buildLastViewedEntries} from './simple-last-viewed.util';
import template from './version-copy-page.component.html';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {LocalUserProfile} from '../../../components/session/local-user-profile';
import {deferSignalViewWrite} from '../../common/util/template-state';
import {NavigationHistoryService} from '../../../components/util/navigation-history.service';
import {StateService} from '@uirouter/core';
import {VersionCopyResourceService} from './version-copy-resource.service';

@Component({
  standalone: false,
  selector: 'adn-version-copy-page',
  template
})
export class VersionCopyPageComponent {
  lastViewed: any[] = [];
  lastViewedObject: any;
  selectFrom = 'LAST_VIEWED';

  loaded = true;
  readonly viewRevision = signal(0);
  jsonGetTest = true;
  objectId: string;
  objectType: any;
  objectTypes = OBJECT_TYPE;
  readonly objectTypeKeys = Object.keys(OBJECT_TYPE);

  err = '';
  objData: any;
  jsonInput = '';
  jsonErr = '';
  jsonInferredObj: any;
  jsonObjectId = '';
  jsonObjectNameNew = '';
  setJsonObj: any;
  jsonObjectIdNew = '';
  jsonGetTestObjFound: string;
  jsonSuccess = '';

  private checkedJsonInput: any;
  private savedObjId = '';
  private savedObjUrlParam = '';

  constructor(
    private readonly $state: StateService,
    @Inject(LocalUserProfile) localUserProfile: any,
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    navigationHistory: NavigationHistoryService,
    private readonly resources: VersionCopyResourceService
  ) {
    this.lastViewed = buildLastViewedEntries(localUserProfile, LocalNetworkProfile, false);
    this.lastViewedObject = _.get(this.lastViewed, [0]);
    this.objectId = navigationHistory.previousParams.id;
    this.objectType = _.find(_.values(OBJECT_TYPE), (ot) => ot.stateName === navigationHistory.previousState?.name);
  }

  get displayLoaded(): boolean {
    this.viewRevision();
    return this.loaded;
  }

  checkObject(isInternalCheck?: boolean): void {
    this.jsonErr = '';
    if (!this.jsonInput) {
      return;
    }

    try {
      this.checkedJsonInput = JSON.parse(this.jsonInput);
    } catch (_err) {
      this.jsonErr = 'Inputted JSON is not valid JSON!';
      return;
    }

    if (!isInternalCheck) {
      this.jsonInferredObj = null;
      if (this.checkedJsonInput.url) {
        this.jsonInferredObj = ObjectTypeFinder.findObjectFromUrl(this.checkedJsonInput.url);
      }
      this.jsonObjectId = this.checkedJsonInput.id || '';
      this.jsonObjectNameNew = this.checkedJsonInput.name || '';
    }
  }

  checkSave(andSave?: boolean): void {
    const objType = this.jsonInferredObj || this.setJsonObj;
    this.checkObject(true);

    if (!objType || !this.resources.supports(objType.type)) {
      this.jsonErr = 'Can\'t find the appropriate end point for a ' + objType;
      return;
    }

    this.jsonGetTest = false;
    this.jsonGetTestObjFound = '';
    const objId = this.jsonObjectIdNew || Uuid.generate();

    const doSave = () => {
      this.checkedJsonInput.id = objId;
      this.checkedJsonInput.name = this.jsonObjectNameNew || Uuid.generate();
      this.resources.save(objType.type, this.checkedJsonInput).subscribe({
        next: () => deferSignalViewWrite(this.viewRevision, () => {
          this.jsonSuccess = 'Save took place';
          this.savedObjId = objId;
          this.savedObjUrlParam = objType.stateName;
        }),
        error: (err: any) => deferSignalViewWrite(this.viewRevision, () => {
          this.jsonErr = err.data;
        })
      });
    };

    if (andSave) {
      doSave();
      return;
    }

    this.resources.get(objType.type, objId).subscribe({
      next: () => deferSignalViewWrite(this.viewRevision, () => {
        this.jsonGetTest = true;
        this.jsonGetTestObjFound = 'FOUND';
      }),
      error: (err: any) => deferSignalViewWrite(this.viewRevision, () => {
        this.jsonGetTest = true;
        this.loaded = true;
        const status = _.get(err, ['status']);
        if (status === 404) {
          this.jsonGetTestObjFound = 'UNFOUNDED';
          doSave();
        } else {
          this.jsonErr = 'An error happened trying to find a duplicate of your object';
        }
      })
    });
  }

  goToObject(): void {
    this.$state.go(this.savedObjUrlParam, {id: this.savedObjId});
  }

  getJson(): void {
    this.err = '';

    const objType = this.selectFrom === 'LAST_VIEWED' ? _.get(this, ['lastViewedObject', 'objectType']) : this.objectType.type;
    const objId = this.selectFrom === 'LAST_VIEWED' ? _.get(this, ['lastViewedObject', 'id']) : this.objectId;

    if (!this.resources.supports(objType)) {
      this.err = 'Can\'t find the appropriate end point for a ' + objType;
      return;
    }
    this.loaded = false;
    this.resources.get(objType, objId).subscribe({
      next: (data: any) => deferSignalViewWrite(this.viewRevision, () => {
        this.loaded = true;
        this.objData = _.cloneDeep(data);

        _.forEach(['updateUser', 'createTime', 'createUser', 'updateUser', 'updateTime', 'network', 'velocity'], (field) => {
          delete this.objData[field];
        });
      }),
      error: (err: any) => deferSignalViewWrite(this.viewRevision, () => {
        this.loaded = true;
        const status = _.get(err, ['status']);
        if (status === 404) {
          this.err = 'Couldn\'t find ' + objType + ' with an ID of ' + objId;
        } else {
          this.err = 'An error has happened finding the JSON';
        }
      })
    });
  }
}
